import {useEffect, useState} from 'react'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {KairosMark} from '@/components/kairos-mark'
import {ChevronGlyph, MenuGlyph, PersonGlyph} from '@/components/ui/icons'
import {Tag} from '@/components/ui/tag'
import {BusinessSwitcherDialog} from '@/components/business-switcher-dialog'
import {MiCuentaDialog} from '@/components/mi-cuenta-dialog'
import {NotificationsBell} from '@/components/notifications-bell'
import {AyudaDePantalla} from '@/components/ayuda-de-pantalla'
import {FeedbackBoton} from '@/components/feedback/feedback-boton'
import {EmailVerifyBanner, EmailVerifyChip} from '@/components/email-verify-banner'
import {ReportarButtons, ReportarDropdown} from '@/components/reportar-dropdown'
import {GodModeContentChip, GodModeExit, GodModeSidebarControls, ViewAsBanner} from '@/components/god-mode-sidebar'
import {SidebarLink} from '@/components/sidebar-link'
import {SidebarSection} from '@/components/sidebar-section'
import {SidebarEmpresa} from '@/components/sidebar-empresa'
import {SidebarSgsst} from '@/components/sidebar-sgsst'
import {AdminConsoleNav} from '@/components/admin-console-nav'
import {useAuth} from '@/hooks/platform/use-auth'
import {useBusiness, useCapabilities, useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useIsPartner} from '@/hooks/inci/aliados/use-partner-panel'
import {useSgsst, useExpedientes} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {useRuta} from '@/hooks/sgsst/resumen/use-ruta'
import {BAND_TONE} from '@/lib/bands'
import {formatDate, formatRatePct} from '@/lib/risk'
import {GodModeProvider} from '@/hooks/platform/use-god-mode'
import {ViewAsProvider, useViewAs} from '@/hooks/platform/use-view-as'
import {LensOutlet} from '@/components/lens-outlet'
import {LAST_BUSINESS_KEY, SYSTEM_BUSINESS_ID} from '@/lib/constants'
import {cn} from '@/lib/utils'

function Avatar() {
  return (
    <span
      aria-hidden="true"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
      <PersonGlyph />
    </span>
  )
}

function UserMenu({
  name,
  onAccount,
  onSwitch,
  onPublicSite,
  onSignOut,
}: {
  name: string
  onAccount: () => void
  onSwitch: () => void
  onPublicSite: () => void
  onSignOut: () => void
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)

  function pick(action: () => void) {
    setOpen(false)
    action()
  }

  return (
    <div className="relative">
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full right-0 z-50 mt-2 min-w-48 overflow-hidden rounded-xl border border-border bg-popover text-foreground shadow-pop">
            <button
              type="button"
              onClick={() => pick(onAccount)}
              className="block w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-secondary">
              {t('account.open')}
            </button>
            <button
              type="button"
              onClick={() => pick(onSwitch)}
              className="block w-full cursor-pointer border-t border-border px-3 py-2 text-left text-sm hover:bg-secondary">
              {t('switcher.open')}
            </button>
            {/* «/» es la app para quien inició sesión: este es el único enlace a la landing. */}
            <button
              type="button"
              data-testid="user-menu-public-site"
              onClick={() => pick(onPublicSite)}
              className="block w-full cursor-pointer border-t border-border px-3 py-2 text-left text-sm hover:bg-secondary">
              {t('account.publicSite')}
            </button>
            <button
              type="button"
              onClick={() => pick(onSignOut)}
              className="block w-full cursor-pointer border-t border-border px-3 py-2 text-left text-sm hover:bg-secondary">
              {t('auth.signOut')}
            </button>
          </div>
        </>
      )}
      <button
        type="button"
        data-testid="user-menu"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
        className="topbar-link flex min-h-11 cursor-pointer items-center gap-2 px-1.5 py-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-0">
        <Avatar />
        <span className="hidden max-w-32 truncate text-sm font-medium sm:block">{name}</span>
      </button>
    </div>
  )
}

/** Elegir un año sellado navega a `/expediente/{año}`; nunca cambia estado global (UX O3). */
function PeriodSelector({businessId, canOperate}: {businessId: string; canOperate: boolean}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const {data: state} = useSgsst(canOperate ? businessId : undefined)
  const {data: expedientes} = useExpedientes(canOperate ? businessId : undefined)
  const currentYear = new Date().getFullYear()
  const periods = state?.periods ?? []
  const openYear = periods.find((p) => !p.is_closed)?.year ?? currentYear
  const sealed = expedientes ?? []

  if (!canOperate) {
    return (
      <span
        data-testid="period-selector-static"
        className="flex min-h-11 items-center rounded-md px-2 font-mono text-xs font-semibold tracking-wide text-muted-foreground md:min-h-0">
        {openYear}
      </span>
    )
  }

  return (
    <div className="relative">
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full right-0 z-50 mt-2 max-h-[70vh] min-w-64 overflow-auto rounded-xl border border-border bg-popover text-foreground shadow-pop">
            <div className="border-b border-border px-3 py-2 font-mono text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
              {t('period.currentLabel')}
            </div>
            <div className="flex items-center gap-2 px-3 py-2 text-sm">
              <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span className="font-mono font-medium">{openYear}</span>
              <span className="text-xs text-muted-foreground">{t('period.currentHint')}</span>
            </div>
            {sealed.length > 0 && (
              <>
                <div className="border-t border-border px-3 py-2 font-mono text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
                  {t('period.sealedLabel')}
                </div>
                {sealed.map((exp) => (
                  <button
                    key={exp.period_year}
                    type="button"
                    data-testid="period-closed-year"
                    onClick={() => {
                      setOpen(false)
                      navigate(`/businesses/${businessId}/expediente/${exp.period_year}`)
                    }}
                    className="flex w-full cursor-pointer flex-col gap-1 px-3 py-2.5 text-left hover:bg-secondary">
                    <span className="flex items-center gap-2 text-sm">
                      <span className="font-mono font-medium">{exp.period_year}</span>
                      <Tag tone={BAND_TONE[exp.band]}>{t(`sgsst.bands.${exp.band}.label`)}</Tag>
                      <span className="font-mono text-xs text-primary tabular-nums">
                        {formatRatePct(exp.total_pct)}
                      </span>
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {exp.closed_by
                        ? t('period.sealedByLine', {
                            date: formatDate(exp.closed_at),
                            name: `${exp.closed_by.name} ${exp.closed_by.last_name}`,
                          })
                        : t('period.sealedOnLine', {date: formatDate(exp.closed_at)})}
                    </span>
                  </button>
                ))}
              </>
            )}
          </div>
        </>
      )}
      <button
        type="button"
        data-testid="period-selector"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
        className="topbar-link flex min-h-11 cursor-pointer items-center gap-1.5 px-2 py-1 font-mono text-xs font-semibold tracking-wide outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-0">
        {openYear}
        <ChevronGlyph size={10} />
      </button>
    </div>
  )
}

function TopBar({
  businessId,
  feedbackBusinessId,
  businessName,
  canOperate,
  userName,
  onMenu,
  onAccount,
  onSwitch,
  onPublicSite,
  onSignOut,
}: {
  businessId: string | undefined
  /** En la consola admin no hay empresa en la URL: se usa la de sistema. */
  feedbackBusinessId: string | undefined
  businessName: string | undefined
  canOperate: boolean
  userName: string
  onMenu: () => void
  onAccount: () => void
  onSwitch: () => void
  onPublicSite: () => void
  onSignOut: () => void
}) {
  const {t} = useTranslation()
  return (
    // En flujo y no sticky: tres pantallas cuelgan su propia barra `position: fixed` del borde superior.
    <header
      data-testid="top-bar"
      className="surface mb-5 flex min-h-[var(--topbar-height)] items-center gap-2 px-3.5 py-2">
      <button
        type="button"
        data-testid="mobile-nav-toggle"
        aria-label={t('nav.openMenu')}
        onClick={onMenu}
        className="topbar-link flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:hidden">
        <MenuGlyph />
      </button>
      <div className="flex min-w-0 items-center gap-2">
        <KairosMark />
        {businessName ? (
          <span
            data-testid="top-bar-business"
            className="min-w-0 truncate font-heading text-base tracking-tight text-foreground">
            {businessName}
          </span>
        ) : (
          <span className="font-heading text-base tracking-tight text-foreground">Kairos</span>
        )}
      </div>
      <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
        {/* El orden de esta fila es memoria (U13): el «?» va antes del correo y la campana, que quedan juntos. */}
        {businessId && (
          <>
            <ReportarButtons businessId={businessId} />
            <ReportarDropdown businessId={businessId} />
            <span aria-hidden="true" className="surface-divider mx-0.5 hidden h-5 border-l sm:block" />
            <PeriodSelector businessId={businessId} canOperate={canOperate} />
          </>
        )}
        {feedbackBusinessId && <FeedbackBoton businessId={feedbackBusinessId} />}
        <AyudaDePantalla />
        <EmailVerifyChip />
        <NotificationsBell placement="down" />
        <UserMenu
          name={userName}
          onAccount={onAccount}
          onSwitch={onSwitch}
          onPublicSite={onPublicSite}
          onSignOut={onSignOut}
        />
      </div>
    </header>
  )
}

function TenantNav({
  businessId,
  canOperate,
  canLeadArea,
  isOwner,
  miEspacio,
}: {
  businessId: string
  canOperate: boolean
  canLeadArea: boolean
  isOwner: boolean
  miEspacio: boolean
}) {
  const {t} = useTranslation()
  const {data: ruta} = useRuta(canOperate ? businessId : undefined)
  // Se desbloquea con el prerrequisito del art. 28 (mejoramiento con acciones) o si el plan ya existe.
  const planTrabajoLocked =
    (ruta?.etapas.find((e) => e.clave === 'plan_trabajo')?.estado ?? 'bloqueada') === 'bloqueada'
  // PQ-7d: la Cuarentena solo la ve quien custodia el dato de salud.
  const custodiaSalud = useCapabilities(canOperate ? businessId : undefined).sgsst_custodiar_salud
  return (
    <>
      <SidebarLink to={`/businesses/${businessId}`} end testId="nav-resumen">
        {t('nav.overview')}
      </SidebarLink>
      {canOperate && (
        <SidebarLink
          to={`/businesses/${businessId}/ruta`}
          testId="nav-ruta"
          showAlert={ruta?.espera_al_usuario ?? false}>
          {t('nav.ruta')}
        </SidebarLink>
      )}

      {canOperate && (
        <>
          <SidebarEmpresa businessId={businessId} isOwner={isOwner} canLeadArea={canLeadArea} />
          <SidebarSgsst businessId={businessId} planTrabajoLocked={planTrabajoLocked} custodiaSalud={custodiaSalud} />
        </>
      )}

      {miEspacio && (
        <SidebarSection id="mi-espacio" context="tenant" label={t('nav.sectionMiEspacio')}>
          {!canOperate && canLeadArea && (
            <SidebarLink to={`/businesses/${businessId}/mi-area`} testId="nav-mi-area">
              {t('nav.miArea')}
            </SidebarLink>
          )}
          <SidebarLink to={`/businesses/${businessId}/mi/informacion`} testId="nav-mi-informacion">
            {t('nav.myInformacion')}
          </SidebarLink>
          <SidebarLink to={`/businesses/${businessId}/mi/acciones`} testId="nav-mi-acciones">
            {t('nav.myAcciones')}
          </SidebarLink>
          <SidebarLink to={`/businesses/${businessId}/mi/cumplimiento`} testId="nav-mi-cumplimiento">
            {t('nav.myCumplimiento')}
          </SidebarLink>
          <SidebarLink to={`/businesses/${businessId}/mi/soportes-solicitudes`} testId="nav-mi-soportes">
            {t('nav.mySoportesSolicitudes')}
          </SidebarLink>
          <SidebarLink to={`/businesses/${businessId}/mi/perfil`} testId="nav-mi-perfil">
            {t('nav.myProfile')}
          </SidebarLink>
        </SidebarSection>
      )}
    </>
  )
}

/**
 * Un admin en modo dios sin «ver como» es dueño virtual y sin ficha: MI ESPACIO es solo para miembros reales.
 * Debe vivir dentro de `ViewAsProvider`.
 */
function TenantNavGate({
  businessId,
  admin,
  canOperate,
  canLeadArea,
  isOwner,
}: {
  businessId: string
  admin: boolean
  canOperate: boolean
  canLeadArea: boolean
  isOwner: boolean
}) {
  const {target} = useViewAs()
  const showMiEspacio = !admin || !!target
  if (!showMiEspacio) {
    return (
      <TenantNav
        businessId={businessId}
        canOperate={canOperate}
        canLeadArea={false}
        isOwner={isOwner}
        miEspacio={false}
      />
    )
  }
  return (
    <TenantNav businessId={businessId} canOperate={canOperate} canLeadArea={canLeadArea} isOwner={isOwner} miEspacio />
  )
}

export function AppLayout() {
  const {t} = useTranslation()
  const {user, signOut} = useAuth()
  const {businessId} = useParams()
  const admin = useIsSystemUser()
  const isPartner = useIsPartner()
  const {pathname} = useLocation()
  const navigate = useNavigate()
  // Para un admin todo lo que no sea `/businesses/*` es consola, incluido el panel de empresa (la empresa
  // es el sujeto, Res. 2764/2022 art. 5); los dos chromes nunca se mezclan.
  const onAdminSection = admin && !pathname.startsWith('/businesses/')
  const routeBusinessId = onAdminSection ? undefined : businessId
  // Las páginas transversales (/notifications) no llevan :businessId: el sidebar usa la última empresa visitada.
  const fallbackBusinessId =
    !admin && !routeBusinessId ? (localStorage.getItem(LAST_BUSINESS_KEY) ?? undefined) : undefined
  const tenantBusinessId = routeBusinessId ?? fallbackBusinessId
  const {data: currentBusiness} = useBusiness(tenantBusinessId)
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  // Solo el Resumen admite la franja del correo; sin empresa cargada esa ruta pinta «Sin acceso» y no la lleva.
  const enResumen = pathname === '/' || (!!currentBusiness && /^\/businesses\/[^/]+$/.test(pathname))
  const canOperate = currentBusiness?.capabilities.sgsst_operar ?? false
  const canLeadArea = currentBusiness?.capabilities.responsable_de_area ?? false
  const isOwner = currentBusiness?.role === 'owner'

  useEffect(() => {
    if (currentBusiness) localStorage.setItem(LAST_BUSINESS_KEY, currentBusiness.id)
  }, [currentBusiness])

  useEffect(() => {
    setMobileNavOpen(false)
  }, [pathname])

  return (
    <GodModeProvider active={admin && !!tenantBusinessId} businessId={tenantBusinessId}>
      <ViewAsProvider active={admin && !!tenantBusinessId} businessId={tenantBusinessId}>
        <div className="flex min-h-screen">
          {mobileNavOpen && (
            <div
              data-testid="mobile-nav-scrim"
              className="scrim-backdrop fixed inset-0 z-40 md:hidden"
              onClick={() => setMobileNavOpen(false)}
            />
          )}
          <aside
            className={cn(
              // w-60 (= --sidenav-width) es fijo: tres barras `position: fixed` usan `md:left-60` y se descuadrarían.
              'sidenav flex h-screen w-60 shrink-0 flex-col overflow-y-auto px-3 pb-6 md:sticky md:top-0',
              'max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:motion-safe:transition-transform',
              mobileNavOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full',
              // La piel es contexto de navegación (consola o tenant), no un tema: por eso es clase del rail y no `data-theme`.
              admin ? 'sidenav-ink' : 'sidenav-tenant border-r border-border'
            )}>
            <div className="sidenav-rule mb-2 flex min-h-[var(--topbar-height)] items-center gap-2 border-b px-2.5">
              <KairosMark />
              <span className="font-heading text-lg tracking-tight">Kairos</span>
              {admin && <Tag tone="inverse">{t('nav.adminTag')}</Tag>}
            </div>

            <nav className="mt-3 flex flex-col gap-0.5">
              {onAdminSection && <AdminConsoleNav />}

              {isPartner && !admin && (
                <SidebarLink to="/partner" testId="nav-partner-panel">
                  {t('nav.partnerPanel')}
                </SidebarLink>
              )}

              {currentBusiness && (
                <>
                  {admin && <GodModeExit businessId={tenantBusinessId!} />}
                  {admin && (
                    <div className="sidenav-section mt-5 mb-1 truncate px-2.5 font-mono text-[10px] font-semibold tracking-[0.12em] uppercase">
                      {currentBusiness.name}
                    </div>
                  )}
                  <TenantNavGate
                    businessId={tenantBusinessId!}
                    admin={admin}
                    canOperate={canOperate}
                    canLeadArea={canLeadArea}
                    isOwner={isOwner}
                  />
                  {admin && businessId && (
                    <GodModeSidebarControls businessId={businessId} businessName={currentBusiness.name} />
                  )}
                </>
              )}
            </nav>
          </aside>

          <main className="min-w-0 flex-1 overflow-y-auto px-4 py-4 md:px-8 md:py-8">
            <TopBar
              businessId={currentBusiness ? tenantBusinessId : undefined}
              feedbackBusinessId={currentBusiness ? tenantBusinessId : admin ? SYSTEM_BUSINESS_ID : undefined}
              businessName={currentBusiness?.name}
              canOperate={canOperate}
              userName={user?.full_name ?? ''}
              onMenu={() => setMobileNavOpen(true)}
              onAccount={() => setAccountOpen(true)}
              onSwitch={() => setSwitcherOpen(true)}
              onPublicSite={() => navigate('/inicio')}
              onSignOut={() => signOut()}
            />
            <div className="mx-auto w-full max-w-5xl">
              {enResumen && <EmailVerifyBanner />}
              {/* Modo dios y «ver como» son seguridad: se comprimen en una franja, nunca se quitan. */}
              {admin && currentBusiness && (
                <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <ViewAsBanner />
                  <GodModeContentChip businessName={currentBusiness.name} />
                </div>
              )}
              <LensOutlet />
            </div>
          </main>

          <BusinessSwitcherDialog open={switcherOpen} onOpenChange={setSwitcherOpen} />
          <MiCuentaDialog open={accountOpen} onOpenChange={setAccountOpen} />
        </div>
      </ViewAsProvider>
    </GodModeProvider>
  )
}
